import AbstractModelType from '../AbstractModelType';
import {
  resolveElasticSearchResults,
  resolveLunrSearchResults,
} from 'utilities';

class SearchResults extends AbstractModelType {
  #book;
  #queryString;
  #results = [];
  #resultsAsKeyValueArrayByMarker = [];
  #resultsCount = 0;
  #hasNoMarkers = true;
  #byTopLevelMarker = new Map();
  #byScore = [];

  constructor({ book, queryString }) {
    super(...arguments);

    this.#book = book;
    this.#queryString = queryString;
  }

  #initialize() {
    // Initialize Map values
    this.#byTopLevelMarker.set(null, []);
    for (let topLevelMarker of this.#book.topLevelMarkers) {
      this.#byTopLevelMarker.set(topLevelMarker, []);
    }

    // Populate Maps
    for (let result of this.#results) {
      this.#byTopLevelMarker
        .get(result.marker?.topLevelMarker ?? null)
        .push(result);
    }

    this.#byTopLevelMarker.forEach((searchResultItems, marker) => {
      if (marker) {
        this.#hasNoMarkers = false;
      }

      // in case we have a null marker, handle that case
      const markerKey = marker ?? {
        label: 'No Chapter Name',
        index: 'no-chapter-name',
        bookLocation: this.#book.documents?.[0]?.bookLocation,
      };

      if (searchResultItems.length) {
        this.#resultsCount += searchResultItems.length;
        this.#resultsAsKeyValueArrayByMarker.push({
          searchResultItems,
          marker: markerKey,
        });
      }
    });

    // Determine the order of the search results by score when not grouping by marker.
    // This is used for Lunr search results, not Elastic search results.
    this.#byScore = this.#results.slice().sort((a, b) => {
      // Sort by score (higher is better).
      if (a.score < b.score) {
        return 1;
      } else if (a.score > b.score) {
        return -1;
      }

      // If the scores are equal, sort by marker index (lower is better).
      if ((a.marker?.index ?? 0) < (b.marker?.index ?? 0)) {
        return -1;
      } else if ((a.marker?.index ?? 0) > (b.marker?.index ?? 0)) {
        return 1;
      }

      // If the scores are equal and the markers are equal,
      // sort by word offset (lower is better).
      if (a.bookLocation.wordOffset < b.bookLocation.wordOffset) {
        return -1;
      } else if (a.bookLocation.wordOffset > b.bookLocation.wordOffset) {
        return 1;
      }

      // This should never happen because it would mean the result is duplicated.
      // But if it does, return 0.
      return 0;
    });

    Object.freeze(this);
  }

  initializeWithElasticResults(elasticSearchResults) {
    this.#results = resolveElasticSearchResults({
      book: this.#book,
      elasticSearchResults,
    });

    this.#initialize();

    return this;
  }

  async initializeWithLunrResults(getDocumentInformation, lunrSearchResults) {
    this.#results = await resolveLunrSearchResults({
      book: this.#book,
      getDocumentInformation,
      lunrSearchResults,
      queryString: this.#queryString,
    });

    this.#initialize();

    return this;
  }

  get byTopLevelMarker() {
    return this.#byTopLevelMarker;
  }

  get hasNoMarkers() {
    return this.#hasNoMarkers;
  }

  get resultsAsKeyValueArrayByMarker() {
    return this.#resultsAsKeyValueArrayByMarker;
  }

  get resultsCount() {
    return this.#resultsCount;
  }

  get byScore() {
    return this.#byScore;
  }
}

export default SearchResults;
